/* Custom Charm Necklace builder (v2). Vanilla custom element, no dependencies.
   Logic follows data/necklace-core.js from the design handoff. */
(() => {
  if (customElements.get('necklace-builder')) return;

  const MAX = 7;
  const LENGTHS = [16, 18, 20, 22, 24];
  const FEEDBACK_MS = 2400;
  const MOBILE_QUERY = '(max-width: 899px)';

  // Chain geometry per layout (README "Chain drawing").
  const GEO = {
    desktop: { height: 440, y0: -10, sagEmpty: 88, sag: 220, size: 120, maxSpacing: 100, pad: 160, ring: 6, base: 2.6, lift: 12 },
    mobile: { height: 196, y0: -6, sagEmpty: 62, sag: 108, size: 58, maxSpacing: 46, pad: 80, ring: 3.5, base: 1.8, lift: 0 },
  };
  const BAIL_X = 0.478; // horizontal center of the bail in the charm PNGs

  // Plain chain color, used only if necklace-chain.js failed to load.
  const METAL = { silver: { base: '#B3B7BC' }, gold: { base: '#CDA54C' } };

  const ICON = {
    left: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m12 19-7-7 7-7M19 12H5"></path></svg>',
    right: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14m-7-7 7 7-7 7"></path></svg>',
    x: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"></path></svg>',
    share: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><path d="m8.59 13.51 6.83 3.98M15.41 6.51l-6.82 3.98"></path></svg>',
  };

  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const finishLabel = (f) => (f === 'gold' ? 'Gold' : 'Silver');

  class NecklaceBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      const cfgEl = this.querySelector('[data-nb-config]');
      if (!cfgEl) return;
      this._ready = true;

      const cfg = JSON.parse(cfgEl.textContent);
      this.cfg = cfg;
      // Event view (product.event template): the CTA places a booth order instead of adding to cart.
      // necklace-event.js handles the order flow; nothing here changes on the normal product page.
      this.event = !!cfg.event;
      this.pricing = Object.assign({ base: 34, charm: 18, lengthFee: 5, standardLength: 18 }, cfg.pricing);
      this.library = cfg.charms.slice().sort((a, b) => a.name.localeCompare(b.name));
      this.byId = Object.fromEntries(cfg.charms.map((c) => [c.id, c]));
      this.presets = (cfg.presets || [])
        .map((p) => Object.assign({}, p, {
          charms: (p.charms || []).map((s) => String(s).trim()).filter((id) => this.byId[id]).slice(0, MAX),
          finish: p.finish === 'gold' ? 'gold' : 'silver',
          length: LENGTHS.includes(Number(p.length)) ? Number(p.length) : 18,
        }))
        .filter((p) => p.name && p.charms.length);

      this.uid = 0;
      this.timers = {};
      this.state = {
        finish: 'silver',
        length: 18,
        charms: [], // [{ uid, id }] in chain order
        selected: null, // index into charms
        filter: 'all',
        query: '',
        presetsOpen: false,
        confirmKey: null,
        cart: 'rest', // rest | loading | added
        error: false,
        breakdownOpen: false,
        shared: false,
        drag: null, // { from, to, x }
      };
      this.restoreFromUrl();

      this.$ = (sel) => this.querySelector(sel);
      this.els = {
        stage: this.$('[data-nb-stage]'),
        svg: this.$('.nb__chain'),
        links: this.$('[data-nb-links]'),
        rings: this.$('[data-nb-rings]'),
        list: this.$('[data-nb-list]'),
        overlay: this.$('[data-nb-overlay]'),
        status: this.$('[data-nb-status]'),
        presetsRow: this.$('[data-nb-presets-row]'),
        price: this.$('[data-nb-price]'),
        priceMobile: this.$('[data-nb-price-mobile]'),
        breakdown: this.$('[data-nb-breakdown]'),
        sheet: this.$('[data-nb-sheet]'),
        details: this.$('[data-nb-details]'),
        add: this.$('[data-nb-add]'),
        addLabel: this.$('[data-nb-add-label]'),
        share: this.$('[data-nb-share]'),
        shareLabel: this.$('[data-nb-share-label]'),
        error: this.$('[data-nb-error]'),
        search: this.$('[data-nb-search]'),
        max: this.$('[data-nb-max]'),
        noResults: this.$('[data-nb-noresults]'),
        tiles: this.$('[data-nb-tiles]'),
        warning: this.$('[data-nb-warning]'),
      };
      this.nodes = new Map(); // uid -> { item, btn, img }
      this.tiles = new Map(); // charm id -> { btn, img, badge }
      this.html = {}; // last innerHTML per region, to skip no-op writes

      this.mq = window.matchMedia(MOBILE_QUERY);
      this.mobile = this.mq.matches;
      this.onMq = () => { this.mobile = this.mq.matches; this.update(); };
      this.mq.addEventListener('change', this.onMq);

      this.buildTiles();
      this.bindStatic();
      this.ro = new ResizeObserver(() => { this.renderChain(); this.fitEmpty(); });
      this.ro.observe(this.els.stage);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.fitEmpty());
      this.update();
      this.checkSetup();
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this.ro && this.ro.disconnect();
      this.mq && this.mq.removeEventListener('change', this.onMq);
      document.removeEventListener('click', this.onOutsideClick);
      Object.values(this.timers).forEach(clearTimeout);
      this.endDrag && this.endDrag();
    }

    /* ---------- state ---------- */

    set(patch, { design = false } = {}) {
      Object.assign(this.state, typeof patch === 'function' ? patch(this.state) : patch);
      if (design && this.state.cart !== 'loading') {
        this.state.cart = 'rest';
        this.state.error = false;
        clearTimeout(this.timers.cart);
      }
      this.update();
    }

    get n() { return this.state.charms.length; }

    restoreFromUrl() {
      const d = new URLSearchParams(window.location.search).get('d');
      if (!d) return;
      const [finish, len, ...ids] = String(d).split('.');
      const length = Number(len);
      this.state.finish = finish === 'gold' ? 'gold' : 'silver';
      this.state.length = LENGTHS.includes(length) ? length : 18;
      this.state.charms = ids.filter((id) => this.byId[id]).slice(0, MAX).map((id) => ({ uid: ++this.uid, id }));
      this.skipIntro = true; // no fade-in for restored charms
    }

    price() {
      const { base, charm, lengthFee, standardLength } = this.pricing;
      const n = this.n;
      const extra = Math.max(0, n - 1);
      const fee = this.state.length !== standardLength ? lengthFee : 0;
      const lines = [{ label: 'Chain + first charm', amount: base }];
      if (extra) lines.push({ label: extra === 1 ? '1 additional charm' : `${extra} additional charms ($${charm} each)`, amount: extra * charm });
      if (fee) lines.push({ label: `${this.state.length}" chain`, amount: fee });
      return { total: base + extra * charm + fee, lines };
    }

    designString() {
      const { finish, length, charms } = this.state;
      return [finish, length, ...charms.map((c) => c.id)].join('.');
    }

    /* ---------- actions ---------- */

    add(id) {
      if (this.n >= MAX) return;
      const uid = ++this.uid;
      this.newUid = uid;
      this.set((s) => ({ charms: [...s.charms, { uid, id }], selected: null }), { design: true });
    }

    select(i) {
      this.set((s) => ({ selected: s.selected === i ? null : i, presetsOpen: s.selected === i ? s.presetsOpen : false, confirmKey: null }));
    }

    move(dir) {
      const { charms, selected } = this.state;
      if (selected === null) return;
      const j = selected + dir;
      if (j < 0 || j >= charms.length) return;
      const next = charms.slice();
      [next[selected], next[j]] = [next[j], next[selected]];
      this.set({ charms: next, selected: j }, { design: true });
      this.focusCharm(next[j].uid);
    }

    remove() {
      const { charms, selected } = this.state;
      if (selected === null) return;
      const next = charms.filter((_, i) => i !== selected);
      this.set({ charms: next, selected: null }, { design: true });
      if (next.length) this.focusCharm(next[Math.min(selected, next.length - 1)].uid);
      else this.els.list.focus({ preventScroll: true });
    }

    loadPreset(p) {
      this.newUid = null;
      this.set({
        charms: p.charms.map((id) => ({ uid: ++this.uid, id })),
        finish: p.finish,
        length: p.length,
        selected: null,
        presetsOpen: false,
        confirmKey: null,
      }, { design: true });
      this.els.list.focus({ preventScroll: true });
    }

    choosePreset(key) {
      const p = this.presets.find((x) => x.key === key);
      if (!p) return;
      if (!this.n) { this.loadPreset(p); return; }
      this.pendingFocus = '[data-k="keep"]';
      this.revealChosen = true;
      this.set({ confirmKey: key });
    }

    // Event view: back to a fresh builder for the next shopper.
    reset() {
      this.newUid = null;
      this.els.search.value = '';
      this.set({
        finish: 'silver',
        length: 18,
        charms: [],
        selected: null,
        filter: 'all',
        query: '',
        presetsOpen: false,
        confirmKey: null,
        breakdownOpen: false,
        shared: false,
        drag: null,
      }, { design: true });
    }

    focusCharm(uid) {
      const node = this.nodes.get(uid);
      if (node) node.btn.focus({ preventScroll: true });
    }

    async share() {
      const url = `${window.location.origin}${window.location.pathname}?d=${encodeURIComponent(this.designString())}`;
      const done = () => {
        this.set({ shared: true });
        clearTimeout(this.timers.share);
        this.timers.share = setTimeout(() => this.set({ shared: false }), FEEDBACK_MS);
      };
      if (this.mobile && navigator.share) {
        try { await navigator.share({ title: document.title, url }); } catch (e) { /* dismissed */ }
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        done();
      } catch (e) {
        // Older browsers or blocked clipboard permission.
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
        document.body.append(ta);
        ta.select();
        const ok = document.execCommand && document.execCommand('copy');
        ta.remove();
        if (ok) done();
      }
    }

    /* ---------- cart ---------- */

    resolveVariant() {
      const variants = this.cfg.variants || [];
      const names = (this.cfg.optionNames || []).map((o) => String(o).toLowerCase());
      const opt = this.cfg.options || {};
      const fi = names.indexOf(String(opt.finish || '').toLowerCase());
      const ci = names.indexOf(String(opt.count || '').toLowerCase());
      // Length option: the configured name first, then the names this store has used ("Chain", formerly "Length").
      const li = [opt.length, 'chain', 'length'].map((n) => names.indexOf(String(n || '').toLowerCase())).find((i) => i >= 0) ?? -1;
      if (ci < 0) return null; // price depends on charm count, so it must be a variant option
      const { finish, length } = this.state;
      const stdLength = this.pricing.standardLength;
      const std = length === stdLength;
      // Length option is either one value per length (16", 18", ...) or Standard / Custom.
      const perLength = li >= 0 && variants.some((v) => {
        const num = parseInt(v.options[li], 10);
        return LENGTHS.includes(num) && num !== stdLength;
      });
      const matches = variants.filter((v) => {
        if (fi >= 0 && String(v.options[fi]).toLowerCase() !== finish) return false;
        if (parseInt(v.options[ci], 10) !== this.n) return false;
        if (li >= 0) {
          const val = String(v.options[li]).toLowerCase();
          const num = parseInt(val, 10);
          if (perLength) return num === length;
          const isStd = val.includes('standard') || num === stdLength;
          if (isStd !== std) return false;
        }
        return true;
      });
      return matches[0] || null;
    }

    // The variant already shows Finish, Charms (count) and Chain (Standard 18" / Custom),
    // so only add what it can't say: the exact custom length and the charm order.
    cartProperties() {
      const { length, charms } = this.state;
      const props = {};
      if (length !== this.pricing.standardLength) props.Length = `${length}"`;
      props.Design = charms.map((c, i) => `${i + 1}. ${this.byId[c.id].name}`).join(' · ');
      props._design = this.designString();
      return props;
    }

    async addToCart() {
      if (!this.n || this.state.cart === 'loading') return;
      const variant = this.resolveVariant();
      if (!variant) {
        console.error('[necklace-builder] No variant matches this design. Check the product options and the section variant settings.');
        this.set({ error: true });
        return;
      }
      this.set({ cart: 'loading', error: false });
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties: this.cartProperties() }] }),
        });
        if (!res.ok) throw new Error(`cart/add.js ${res.status}`);
        const item = await res.json();
        this.set({ cart: 'added' });
        clearTimeout(this.timers.cart);
        this.timers.cart = setTimeout(() => this.set({ cart: 'rest' }), FEEDBACK_MS);
        this.dispatchEvent(new CustomEvent('necklace-builder:added', { bubbles: true, detail: { item, design: this.designString() } }));
        this.afterAdd();
      } catch (err) {
        console.error('[necklace-builder]', err);
        this.set({ cart: 'rest', error: true });
      }
    }

    async afterAdd() {
      const mode = this.cfg.afterAdd || 'drawer';
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      if (mode === 'stay') return;
      if (mode === 'cart') { window.location.href = `${root}cart`; return; }

      // Broadcast (the store's "Relay" theme): theme:cart:refresh re-renders the drawer and cart count,
      // then the theme fires theme:product:added, which opens the drawer.
      if (window.theme && window.theme.hasCartDrawer) {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        return;
      }

      const trigger = this.cfg.drawerSelector && document.querySelector(this.cfg.drawerSelector);
      if (trigger) { trigger.click(); return; }
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
      if (window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.openCart === 'function') {
        try { await window.Shopify.actions.openCart(); return; } catch (e) { /* fall through */ }
      }
      window.location.href = `${root}cart`;
    }

    // Theme editor only: flag setup problems so they are caught before launch.
    checkSetup() {
      if (!(window.Shopify && window.Shopify.designMode) || !this.els.warning) return;
      const problems = [];
      const names = (this.cfg.optionNames || []).map((o) => String(o).toLowerCase());
      if (names.indexOf(String(this.cfg.options.count || '').toLowerCase()) < 0) {
        problems.push(`The product has no "${esc(this.cfg.options.count)}" option, so Add to Cart can't pick a priced variant.`);
      } else {
        const saved = this.state;
        const bad = [];
        ['silver', 'gold'].forEach((finish) => [18, 20].forEach((length) => {
          for (let n = 1; n <= MAX; n++) {
            this.state = Object.assign({}, saved, { finish, length, charms: Array.from({ length: n }, () => ({ id: this.library[0].id })) });
            const v = this.resolveVariant();
            const want = this.price().total * 100;
            if (!v) bad.push(`${finishLabel(finish)} ${n} charm(s) ${length}" has no variant`);
            else if (Number(v.price) !== want) bad.push(`${finishLabel(finish)} ${n} charm(s) ${length}" is $${(v.price / 100).toFixed(2)}, expected $${want / 100}`);
          }
        }));
        this.state = saved;
        if (bad.length) problems.push(`Variant prices don't match the builder: ${bad.slice(0, 4).map(esc).join('; ')}${bad.length > 4 ? ` and ${bad.length - 4} more` : ''}.`);
      }
      if (problems.length) {
        this.els.warning.innerHTML = `<strong>Necklace builder setup (only visible in the theme editor):</strong> ${problems.join(' ')}`;
        this.els.warning.hidden = false;
      }
    }

    /* ---------- events ---------- */

    bindStatic() {
      // Finish and length radio groups (roving tabindex, arrow keys select).
      this.querySelectorAll('[data-nb-radios]').forEach((group) => {
        const kind = group.dataset.nbRadios;
        const radios = [...group.querySelectorAll('[role="radio"]')];
        const choose = (btn) => {
          const value = kind === 'length' ? Number(btn.dataset.value) : btn.dataset.value;
          if (this.state[kind] !== value) this.set({ [kind]: value }, { design: true });
        };
        radios.forEach((btn, i) => {
          btn.addEventListener('click', () => choose(btn));
          btn.addEventListener('keydown', (e) => {
            const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
            if (!step) return;
            e.preventDefault();
            const next = radios[(i + step + radios.length) % radios.length];
            choose(next);
            next.focus();
          });
        });
      });

      this.querySelectorAll('[data-filter]').forEach((btn) => {
        btn.addEventListener('click', () => this.set({ filter: btn.dataset.filter }));
      });

      // Typing a search looks across every category, and the filter pills show All to match.
      this.els.search.addEventListener('input', (e) => this.set((s) => ({ query: e.target.value, filter: e.target.value.trim() ? 'all' : s.filter })));

      this.els.add.addEventListener('click', () => {
        if (this.els.add.getAttribute('aria-disabled') === 'true') return;
        if (this.event) {
          this.dispatchEvent(new CustomEvent('necklace-builder:order', { bubbles: true, detail: { design: this.designString() } }));
          return;
        }
        this.addToCart();
      });
      this.els.share.addEventListener('click', () => this.share());
      this.els.details.addEventListener('click', () => this.set((s) => ({ breakdownOpen: !s.breakdownOpen })));

      // Delegated clicks for regions rendered from strings.
      const onRegionClick = (e) => {
        const btn = e.target.closest('[data-act]');
        if (!btn || !this.contains(btn)) return;
        if (btn.getAttribute('aria-disabled') === 'true') return;
        const act = btn.dataset.act;
        if (act === 'left') this.move(-1);
        else if (act === 'right') this.move(1);
        else if (act === 'remove') this.remove();
        else if (act === 'done') { const c = this.state.charms[this.state.selected]; this.set({ selected: null }); if (c) this.focusCharm(c.uid); }
        else if (act === 'clear') { this.set({ charms: [], selected: null }, { design: true }); this.els.list.focus({ preventScroll: true }); }
        else if (act === 'toggle-presets') {
          // Desktop opens the panel over the stage, so move focus into it; the phone row opens in place.
          if (!this.state.presetsOpen && !this.mobile) this.pendingFocus = '[data-k="close"]';
          this.set((s) => ({ presetsOpen: !s.presetsOpen, confirmKey: null }));
        }
        else if (act === 'close-presets') { this.pendingFocus = '[data-k="toggle"]'; this.set({ presetsOpen: false, confirmKey: null }); }
        else if (act === 'preset') this.choosePreset(btn.dataset.key);
        else if (act === 'swap') { const p = this.presets.find((x) => x.key === this.state.confirmKey); if (p) this.loadPreset(p); }
        else if (act === 'keep') { this.pendingFocus = `[data-k="preset-${this.state.confirmKey}"]`; this.set({ confirmKey: null }); }
        else if (act === 'share') this.share();
      };
      [this.els.overlay, this.els.status, this.els.presetsRow].forEach((el) => el.addEventListener('click', onRegionClick));

      // A click or tap anywhere outside the selected charm and its toolbar finishes arranging, same as Done.
      // Uses click (not pointerdown) so scrolling the page on a phone doesn't drop the selection.
      this.onOutsideClick = (e) => {
        if (this.state.selected === null) return;
        // composedPath is fixed when the click starts, so it still works after the toolbar re-renders.
        const inside = e.composedPath().some((n) => n === this.els.status || (n.classList && n.classList.contains('nb__placed')));
        if (!inside) this.set({ selected: null });
      };
      document.addEventListener('click', this.onOutsideClick);

      // Esc from the selection toolbar returns to the charm.
      this.els.status.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || this.state.selected === null) return;
        const c = this.state.charms[this.state.selected];
        this.set({ selected: null });
        if (c) this.focusCharm(c.uid);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.state.presetsOpen && !e.defaultPrevented && !this.els.status.contains(e.target)) {
          this.pendingFocus = '[data-k="toggle"]';
          this.set({ presetsOpen: false, confirmKey: null });
        }
      });
    }

    onCharmClick(uid) {
      if (this.suppressClick) { this.suppressClick = false; return; }
      const i = this.state.charms.findIndex((c) => c.uid === uid);
      if (i >= 0) this.select(i);
    }

    onCharmKey(e, uid) {
      const i = this.state.charms.findIndex((c) => c.uid === uid);
      if (i < 0 || this.state.selected !== i) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.move(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.move(1); }
      else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); this.remove(); }
      else if (e.key === 'Escape') { e.preventDefault(); this.set({ selected: null }); }
    }

    // Desktop only: mouse drag along the chain to reorder.
    startDrag(e, uid) {
      if (this.mobile || e.pointerType !== 'mouse' || e.button !== 0) return;
      const from = this.state.charms.findIndex((c) => c.uid === uid);
      if (from < 0 || this.n < 2) return;
      const startX = e.clientX;
      let active = false;
      const onMove = (ev) => {
        if (!active && Math.abs(ev.clientX - startX) < 6) return;
        active = true;
        const rect = this.els.stage.getBoundingClientRect();
        const W = this.els.stage.clientWidth;
        const x = Math.max(40, Math.min(W - 40, ev.clientX - rect.left - this.els.stage.clientLeft));
        const spacing = this.spacing(W);
        const to = Math.max(0, Math.min(this.n - 1, Math.round((x - W / 2) / spacing + (this.n - 1) / 2)));
        this.set({ drag: { from, to, x }, selected: null });
      };
      const onUp = () => {
        this.endDrag();
        if (!active) return;
        this.suppressClick = true;
        setTimeout(() => { this.suppressClick = false; }, 0);
        const { drag, charms } = this.state;
        if (!drag) return;
        const next = charms.slice();
        const [m] = next.splice(drag.from, 1);
        next.splice(drag.to, 0, m);
        this.set({ charms: next, drag: null }, { design: drag.from !== drag.to });
      };
      this.endDrag = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        this.endDrag = null;
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    }

    /* ---------- rendering ---------- */

    update() {
      const s = this.state;
      if (s.selected !== null && s.selected >= this.n) s.selected = null;
      if (!this.n && s.presetsOpen) s.presetsOpen = false;
      if (!this.n) s.confirmKey = null;
      this.renderChain();
      this.renderOverlay();
      this.renderStatus();
      this.renderPresetsRow();
      this.renderOptions();
      this.renderLibrary();
      this.renderBuy();
      this.fitEmpty();
      if (this.pendingFocus) {
        const el = this.querySelector(this.pendingFocus);
        this.pendingFocus = null;
        if (el) el.focus({ preventScroll: true });
      }
    }

    // Write innerHTML only when it changed, and keep focus on the same keyed control.
    patch(el, key, html) {
      if (this.html[key] === html) return false;
      this.html[key] = html;
      const active = document.activeElement;
      const k = active && el.contains(active) ? active.getAttribute('data-k') : null;
      el.innerHTML = html;
      if (k) {
        const again = el.querySelector(`[data-k="${k}"]`);
        if (again) again.focus({ preventScroll: true });
      }
      return true;
    }

    spacing(W) {
      const g = this.mobile ? GEO.mobile : GEO.desktop;
      return Math.min(g.maxSpacing, (W - g.pad) / 6);
    }

    renderChain() {
      const { stage, svg, links, rings, list } = this.els;
      const g = this.mobile ? GEO.mobile : GEO.desktop;
      const W = stage.clientWidth;
      const H = stage.clientHeight || g.height;
      if (!W) return;
      const { charms, selected, drag, finish } = this.state;
      const n = charms.length;

      const x0 = -30;
      const x1 = W + 30;
      const y0 = g.y0;
      const sag = n ? g.sag : g.sagEmpty;
      const yc = 2 * sag - y0;
      const yAt = (x) => { const t = (x - x0) / (x1 - x0); return (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * yc + t * t * y0; };
      const spacing = this.spacing(W);
      const layout = this.mobile ? 'mobile' : 'desktop';
      const chain = window.NecklaceChain;

      svg.setAttribute('width', W);
      svg.setAttribute('height', H);
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      // The links only change with width, sag, finish or layout, so redraw them only then.
      const linksKey = `${W}|${sag}|${finish}|${layout}`;
      if (this.linksKey !== linksKey) {
        this.linksKey = linksKey;
        const curve = { x0, y0, cx: W / 2, cy: yc, x1, y1: y0 };
        links.innerHTML = chain
          ? chain.links(curve, finish, layout)
          : `<path d="M${x0},${y0} Q${W / 2},${yc} ${x1},${y0}" fill="none" stroke="${METAL[finish].base}" stroke-width="${g.base}" stroke-linecap="round"/>`;
      }

      // Visual order: during a drag the dragged charm leaves its slot and the rest close up around the drop slot.
      const order = charms.map((_, i) => i);
      if (drag) { order.splice(drag.from, 1); order.splice(drag.to, 0, drag.from); }

      const ringList = [];
      const seen = new Set();
      order.forEach((i, slot) => {
        const c = charms[i];
        seen.add(c.uid);
        let node = this.nodes.get(c.uid);
        if (!node) node = this.createCharmNode(c.uid);

        const x = W / 2 + (slot - (n - 1) / 2) * spacing;
        const y = yAt(x);
        const isDrag = drag && drag.from === i;
        if (!isDrag && node.loaded) ringList.push({ cx: x, cy: y + g.ring - 0.5, r: g.ring });
        const left = isDrag ? drag.x - g.size * BAIL_X : x - g.size * BAIL_X;
        const top = isDrag ? yAt(drag.x) + 2 * g.ring - 2 - g.lift : y + 2 * g.ring - 2;

        const { item, btn, img } = node;
        item.style.left = `${Math.round(left)}px`;
        item.style.top = `${Math.round(top)}px`;
        item.style.width = `${g.size}px`;
        item.style.height = `${g.size}px`;
        item.classList.toggle('is-dragging', !!isDrag);
        item.classList.toggle('is-sliding', !!drag && !isDrag);
        item.classList.toggle('is-dimmed', selected !== null && selected !== i);
        const src = this.byId[c.id].img[finish].lg;
        if (img.getAttribute('src') !== src) img.setAttribute('src', src);
        const isSel = selected === i;
        btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
        btn.setAttribute('aria-label', `${this.byId[c.id].name}, position ${i + 1} of ${n}${isSel ? ', selected' : ''}`);
      });
      const ringsHtml = chain ? chain.rings(ringList, finish, layout) : '';
      if (this.ringsHtml !== ringsHtml) {
        this.ringsHtml = ringsHtml;
        rings.innerHTML = ringsHtml;
      }

      this.nodes.forEach((node, uid) => {
        if (!seen.has(uid)) { node.item.remove(); this.nodes.delete(uid); }
      });

      // Keep DOM order equal to chain order (tab order, screen readers). Skip while dragging.
      if (!drag) {
        const active = document.activeElement;
        charms.forEach((c, i) => {
          const { item } = this.nodes.get(c.uid);
          if (list.children[i] !== item) list.insertBefore(item, list.children[i] || null);
        });
        if (active && list.contains(active) && document.activeElement !== active) active.focus({ preventScroll: true });
      }
      this.skipIntro = false;
    }

    createCharmNode(uid) {
      const item = document.createElement('div');
      item.className = 'nb__placed';
      item.setAttribute('role', 'listitem');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'nb__placed-btn';
      const img = document.createElement('img');
      img.alt = '';
      img.draggable = false;
      img.decoding = 'async';
      btn.append(img);
      item.append(btn);
      // Stay hidden (charm and jump ring) until the chain-size image has loaded, so a first-time
      // charm never shows an empty ring. A newly added charm fades in once it's ready.
      const node = { item, btn, img, loaded: false };
      const animate = uid === this.newUid && !this.skipIntro;
      item.classList.add('is-loading');
      const ready = () => {
        if (node.loaded) return;
        node.loaded = true;
        item.classList.remove('is-loading');
        if (animate) {
          item.classList.add('is-new');
          img.addEventListener('animationend', () => item.classList.remove('is-new'), { once: true });
        }
        this.renderChain();
      };
      img.addEventListener('load', ready);
      img.addEventListener('error', ready);
      node.ready = ready;
      btn.addEventListener('click', () => this.onCharmClick(uid));
      btn.addEventListener('keydown', (e) => this.onCharmKey(e, uid));
      btn.addEventListener('pointerdown', (e) => this.startDrag(e, uid));
      this.els.list.append(item);
      this.nodes.set(uid, node);
      return node;
    }

    presetCard(p, { meta, size }) {
      const chosen = this.state.confirmKey === p.key;
      const minis = p.charms.map((id) => `<img src="${esc(this.byId[id].img[p.finish].sm)}" alt="" width="${size}" height="${size}" loading="lazy">`).join('');
      const aria = `${p.name}. ${p.line} ${p.charms.length} charms, ${p.length} inch chain, ${p.finish}.`;
      return `<button type="button" class="nb__preset${chosen ? ' is-chosen' : ''}" data-act="preset" data-key="${esc(p.key)}" data-k="preset-${esc(p.key)}" aria-label="${esc(aria)}">`
        + `<span class="nb__minis">${minis}</span>`
        + `<span class="nb__preset-name">${esc(p.name)}</span>`
        + (p.line ? `<span class="nb__preset-line">${esc(p.line)}</span>` : '')
        + (meta ? `<span class="nb__preset-meta">${p.charms.length} charms · ${p.length}" · ${finishLabel(p.finish)}</span>` : '')
        + '</button>';
    }

    confirmHtml() {
      const order = this.mobile
        ? '<button type="button" class="nb__swap" data-act="swap" data-k="swap">Swap It</button><button type="button" class="nb__text-btn" data-act="keep" data-k="keep">Keep Mine</button>'
        : '<button type="button" class="nb__text-btn" data-act="keep" data-k="keep">Keep Mine</button><button type="button" class="nb__swap" data-act="swap" data-k="swap">Swap It</button>';
      return `<div class="nb__confirm" role="alertdialog" aria-label="Replace design" aria-describedby="${this.id}-confirm-text">`
        + `<p id="${this.id}-confirm-text">Swap your chain for this one? Your current design gets cleared.</p>`
        + `<div class="nb__confirm-actions">${order}</div></div>`;
    }

    renderOverlay() {
      const { selected, presetsOpen, confirmKey, charms } = this.state;
      const n = charms.length;
      const hasSel = selected !== null;
      let html = '';
      if (!n) {
        html += '<div class="nb__empty"><p class="nb__empty-title">Your chain\'s empty.</p>'
          + '<p class="nb__empty-text">Start with your own cat, or begin with one of ours.</p>';
        if (!this.mobile && this.presets.length) {
          html += '<h2 class="nb__eyebrow nb__eyebrow--rule">Start From One of Ours</h2>'
            + `<div class="nb__preset-grid">${this.presets.map((p) => this.presetCard(p, { meta: false, size: 26 })).join('')}</div>`;
        }
        html += '</div>';
      }
      if (hasSel) {
        const c = charms[selected];
        html += `<p class="nb__sel-label" aria-hidden="true">${esc(this.byId[c.id].name)} · ${selected + 1} of ${n}</p>`;
      }
      if (!this.mobile && n && presetsOpen) {
        html += '<div class="nb__panel" role="region" aria-label="Start From One of Ours">'
          + '<div class="nb__panel-head"><h2 class="nb__panel-title">Start From One of Ours</h2>'
          + `<button type="button" class="nb__icon-btn" data-act="close-presets" data-k="close" aria-label="Close starter designs">${ICON.x}</button></div>`
          + (confirmKey ? this.confirmHtml() : '')
          + `<div class="nb__preset-grid">${this.presets.map((p) => this.presetCard(p, { meta: true, size: 30 })).join('')}</div>`
          + '</div>';
      }
      this.patch(this.els.overlay, 'overlay', html);
    }

    // Desktop empty state: if the starter cards don't fit inside the stage, drop their one-line descriptions.
    fitEmpty() {
      // Starter designs panel (charms on the chain): same idea when the swap question pushes cards down.
      const panel = this.els.overlay.querySelector('.nb__panel');
      if (panel) {
        panel.classList.remove('is-compact');
        if (panel.scrollHeight > panel.clientHeight + 1) panel.classList.add('is-compact');
      }
      const empty = this.els.overlay.querySelector('.nb__empty');
      if (!empty || this.mobile) return;
      empty.classList.remove('is-compact');
      const limit = this.els.stage.clientHeight - 12;
      if (empty.offsetTop + empty.offsetHeight > limit) empty.classList.add('is-compact');
    }

    renderStatus() {
      const { selected, charms, presetsOpen, shared } = this.state;
      const n = charms.length;
      let html;
      if (selected !== null) {
        const name = esc(this.byId[charms[selected].id].name);
        const first = selected === 0;
        const last = selected === n - 1;
        html = `<div class="nb__toolbar" role="toolbar" aria-label="Arrange ${name}">`
          + `<button type="button" class="nb__outline-btn" data-act="left" data-k="left" aria-disabled="${first}">${ICON.left}Move Left</button>`
          + `<button type="button" class="nb__outline-btn" data-act="right" data-k="right" aria-disabled="${last}">Move Right${ICON.right}</button>`
          + `<button type="button" class="nb__outline-btn" data-act="remove" data-k="remove">${ICON.x}Remove</button>`
          + (this.mobile ? '' : '<button type="button" class="nb__text-btn nb__done" data-act="done" data-k="done">Done</button>')
          + '</div>';
      } else {
        const hintText = this.event ? 'Tap one to move it' : 'Drag to reorder, or click one for more';
        const hint = !this.mobile && n > 1 ? `<span class="nb__status-hint"> · ${hintText}</span>` : '';
        html = `<p class="nb__status-line">${n ? `${n} of ${MAX} charms` : `Up to ${MAX} charms`}${hint}</p>`;
        if (n) {
          html += '<div class="nb__status-actions">'
            + (this.presets.length ? `<button type="button" class="nb__text-btn" data-act="toggle-presets" data-k="toggle" aria-expanded="${presetsOpen}">Start From One of Ours</button>` : '')
            + (this.mobile && this.event ? ''
              : this.mobile
              ? `<button type="button" class="nb__share-short" data-act="share" data-k="share" aria-label="Share This Design">${ICON.share}<span aria-live="polite">${shared ? 'Copied' : 'Share'}</span></button>`
              : '<button type="button" class="nb__text-btn" data-act="clear" data-k="clear">Clear Chain</button>')
            + '</div>';
        }
      }
      this.patch(this.els.status, 'status', html);
    }

    renderPresetsRow() {
      const row = this.els.presetsRow;
      const { presetsOpen, confirmKey } = this.state;
      const show = this.mobile && this.presets.length && (!this.n || presetsOpen);
      row.hidden = !show;
      if (!show) return;
      // Built once; the card row stays in place so its scroll position survives updates.
      if (!this.rowHead) {
        row.innerHTML = '<div data-nb-row-head></div>'
          + `<div class="nb__scroller">${this.presets.map((p) => this.presetCard(p, { meta: false, size: 26 })).join('')}</div>`;
        this.rowHead = row.querySelector('[data-nb-row-head]');
        this.scroller = row.querySelector('.nb__scroller');
      }
      this.patch(this.rowHead, 'presets-head', '<div class="nb__presets-head"><h2 class="nb__eyebrow">Start From One of Ours</h2>'
        + (this.n ? `<button type="button" class="nb__icon-btn" data-act="close-presets" data-k="close" aria-label="Close starter designs">${ICON.x}</button>` : '')
        + '</div>'
        + (confirmKey && this.n ? this.confirmHtml() : ''));
      this.scroller.querySelectorAll('.nb__preset').forEach((card) => {
        card.classList.toggle('is-chosen', card.dataset.key === confirmKey);
      });
      if (confirmKey && this.revealChosen) {
        const sc = this.scroller;
        const card = sc.querySelector(`[data-key="${confirmKey}"]`);
        if (card) {
          const left = sc.scrollLeft + card.getBoundingClientRect().left - sc.getBoundingClientRect().left - 16;
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          sc.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
        }
      }
      this.revealChosen = false;
    }

    renderOptions() {
      this.querySelectorAll('[data-nb-radios]').forEach((group) => {
        const kind = group.dataset.nbRadios;
        group.querySelectorAll('[role="radio"]').forEach((btn) => {
          const on = String(this.state[kind]) === btn.dataset.value;
          btn.setAttribute('aria-checked', on ? 'true' : 'false');
          btn.tabIndex = on ? 0 : -1;
        });
      });
      this.querySelectorAll('[data-filter]').forEach((btn) => {
        btn.setAttribute('aria-pressed', btn.dataset.filter === this.state.filter ? 'true' : 'false');
      });
      const search = this.els.search;
      const ph = this.mobile ? search.dataset.placeholderMobile : search.dataset.placeholderDesktop;
      if (ph && search.placeholder !== ph) search.placeholder = ph;
    }

    preload(id) {
      const src = this.byId[id].img[this.state.finish].lg;
      this.preloaded = this.preloaded || new Set();
      if (this.preloaded.has(src)) return;
      this.preloaded.add(src);
      new Image().src = src;
    }

    buildTiles() {
      const frag = document.createDocumentFragment();
      this.library.forEach((c) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'nb__tile';
        btn.dataset.id = c.id;
        btn.innerHTML = `<img alt="" width="76" height="76" loading="lazy" decoding="async"><span class="nb__tile-name">${esc(c.name)}</span><span class="nb__badge" aria-hidden="true" hidden></span>`;
        btn.addEventListener('click', () => {
          if (btn.getAttribute('aria-disabled') === 'true') return;
          this.add(c.id);
        });
        // Start fetching the chain-size image on hover, focus or press, before the click lands.
        ['pointerenter', 'pointerdown', 'focus'].forEach((type) => btn.addEventListener(type, () => this.preload(c.id)));
        frag.append(btn);
        this.tiles.set(c.id, { btn, img: btn.querySelector('img'), badge: btn.querySelector('.nb__badge') });
      });
      this.els.tiles.append(frag);
    }

    renderLibrary() {
      const { charms, filter, query, finish } = this.state;
      const atMax = charms.length >= MAX;
      const counts = {};
      charms.forEach((c) => { counts[c.id] = (counts[c.id] || 0) + 1; });
      const q = query.trim().toLowerCase();
      const qId = q.replace(/\s+/g, '-');
      let visible = 0;
      this.library.forEach((c) => {
        const t = this.tiles.get(c.id);
        const show = (filter === 'all' || c.category === filter) && (!q || c.name.toLowerCase().includes(q) || c.id.includes(qId));
        t.btn.hidden = !show;
        if (show) visible++;
        const src = c.img[finish].sm;
        if (t.img.getAttribute('src') !== src) t.img.setAttribute('src', src);
        const count = counts[c.id] || 0;
        t.badge.hidden = !count;
        t.badge.textContent = count ? `×${count}` : '';
        t.btn.setAttribute('aria-disabled', atMax ? 'true' : 'false');
        t.btn.setAttribute('aria-label', `Add ${c.name}${count ? `, ${count} on chain` : ''}`);
      });
      this.els.max.hidden = !atMax;
      this.els.noResults.hidden = visible > 0;
    }

    renderBuy() {
      const { cart, shared, breakdownOpen, error } = this.state;
      const empty = !this.n;
      const { total, lines } = this.price();
      const money = (v) => `$${v}`;
      this.els.price.textContent = money(total);
      this.els.priceMobile.textContent = money(total);
      this.patch(this.els.breakdown, 'breakdown', lines.map((l) => `<span>${esc(l.label)} ${money(l.amount)}</span>`).join(''));
      this.patch(this.els.sheet, 'sheet', lines.map((l) => `<span><span>${esc(l.label)}</span><span>${money(l.amount)}</span></span>`).join(''));
      this.els.sheet.hidden = !(this.mobile && breakdownOpen);
      this.els.details.setAttribute('aria-expanded', breakdownOpen ? 'true' : 'false');

      const add = this.els.add;
      add.dataset.state = cart;
      add.setAttribute('aria-disabled', empty || cart === 'loading' ? 'true' : 'false');
      add.setAttribute('aria-busy', cart === 'loading' ? 'true' : 'false');
      this.els.addLabel.textContent = this.event ? 'Order This One'
        : cart === 'loading' ? 'Adding to Cart' : cart === 'added' ? 'Added to Cart' : 'Add to Cart';

      // Event view shares from the success screen only.
      this.els.share.hidden = empty || this.event;
      this.els.shareLabel.textContent = shared ? 'Link Copied' : 'Share This Design';

      this.els.error.hidden = !error;
      if (error) this.els.error.textContent = "That didn't make it into your cart. Try again.";
    }
  }

  customElements.define('necklace-builder', NecklaceBuilder);
})();
